import { useParams, Link, useNavigate } from 'react-router-dom';
import useWorkspace from '../../hooks/useWorkspace';
import useCodices from '../../hooks/useCodices';
import useDisclosure from '../../hooks/useDisclosure';
import { paths } from '../../routes/paths';
import { chaptersApi } from '../../lib/api';
import PageHeader from '../../components/ui/PageHeader/PageHeader';
import Button from '../../components/ui/Button/Button';
import EmptyState from '../../components/ui/EmptyState/EmptyState';
import Icon from '../../components/ui/Icon/Icon';
import CodexGrid from '../../components/codex/CodexGrid/CodexGrid';
import CreateCodexModal from '../../components/codex/CreateCodexModal/CreateCodexModal';
import styles from './WorkspacePage.module.css';

export default function WorkspacePage() {
  const { workspaceId } = useParams();
  const navigate = useNavigate();
  const { workspace, loading: wsLoading, error: wsError } = useWorkspace(workspaceId);
  const { codices, loading, error, createCodex, patchCodex } = useCodices(workspaceId);

  const createModal = useDisclosure();

  const title = workspace?.name || (wsLoading ? 'loading...' : 'workspace');
  const openCodex = (codexId) => navigate(paths.codex(workspaceId, codexId));

  return (
    <>
      <Link to={paths.workspaces} className={styles.back}>
        workspaces
      </Link>

      <PageHeader
        kicker="workspace"
        title={title}
        subtitle="// codices in this workspace"
        actions={
          <>
            <Button
              variant="secondary"
              leftIcon={<Icon name="Settings" size={14} />}
              onClick={() => navigate(paths.workspaceSettings(workspaceId))}
            >
              Settings
            </Button>
            <Button leftIcon={<Icon name="Plus" size={14} />} onClick={createModal.open}>
              New codex
            </Button>
          </>
        }
      />

      {wsError && (
        <EmptyState icon="AlertTriangle" title="Could not load workspace" description={wsError} />
      )}

      {!wsError && loading && <p className={styles.loading}>loading codices</p>}

      {!wsError && !loading && error && (
        <EmptyState icon="AlertTriangle" title="Could not load codices" description={error} />
      )}

      {!wsError && !loading && !error && codices.length === 0 && (
        <EmptyState
          icon="BookOpen"
          title="No codices yet"
          description="Create your first codex to start writing chapters."
          action={
            <Button leftIcon={<Icon name="Plus" size={14} />} onClick={createModal.open}>
              Create codex
            </Button>
          }
        />
      )}

      {!wsError && !loading && !error && codices.length > 0 && (
        <CodexGrid
          codices={codices}
          onOpen={openCodex}
          onPatch={patchCodex}
        />
      )}

      <CreateCodexModal
        isOpen={createModal.isOpen}
        onClose={createModal.close}
        onCreate={async (title, type, subtype) => {
          const id = await createCodex(title, type, subtype);
          // `plain` screenplays are 1-to-1 with a single chapter — bootstrap
          // it now so the writer drops straight into the editor instead of
          // an empty tree. Best-effort: if the create fails, the codex page
          // still works (the user can create the chapter manually).
          if (id && type === 'screenplay' && subtype === 'plain') {
            try {
              await chaptersApi.create({ codexId: id, title });
            } catch (err) {
              console.warn('failed to seed plain chapter', err);
            }
          }
          if (id) openCodex(id);
        }}
      />
    </>
  );
}
